<script setup>
/**
 * Aceleres 2026-08-17: figurinha WebP ANIMADA rodando 3 vezes e parando, a
 * mesma regra do GIF (GIF_MAX_LOOPS em Video.vue) e da figurinha vetorial
 * (STICKER_MAX_LOOPS em LottieStickerCanvas.vue).
 *
 * Por que existe um componente para isto
 * -------------------------------------
 * O numero de repeticoes de um WebP animado nao e escolha da pagina: vem
 * gravado no proprio ficheiro, no campo `loop_count` do bloco ANIM. Nas
 * figurinhas do WhatsApp esse campo e 0, que significa INFINITO — conferido
 * nos ficheiros guardados aqui (136, 42 e 139 quadros, todos com
 * `loop_count=0`). O navegador obedece ao ficheiro, e nao ha atributo de HTML
 * nem propriedade de CSS que limite isso num `<img>`.
 *
 * Entao a unica forma de mandar nas voltas e desenhar a animacao nos mesmos:
 * o `ImageDecoder` entrega quadro a quadro e o desenho vai para um `<canvas>`
 * que fica por cima do `<img>` original. Quem conta as voltas passa a ser
 * este componente.
 *
 * O `<img>` continua no DOM e continua sendo quem DA O TAMANHO da bolha — a
 * moldura da figurinha ja depende dele, e reimplementar essa medida aqui
 * seria uma segunda fonte para o mesmo numero. Ele so fica invisivel quando o
 * canvas assume (`visibility`, nao `display`: o espaco tem de continuar la).
 *
 * Degradacao: se o navegador nao tiver `ImageDecoder`, se a descarga falhar,
 * se o ficheiro for estatico ou se qualquer passo der erro, o componente nao
 * emite `active`, o `<img>` fica visivel e o comportamento e exatamente o de
 * antes. Nada de figurinha em branco por causa desta melhoria.
 */
import { ref, onMounted, onBeforeUnmount } from 'vue';

const props = defineProps({
  url: {
    type: String,
    required: true,
  },
  replayOnHover: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['active', 'inactive']);

const STICKER_MAX_LOOPS = 3;

const canvas = ref(null);
const isActive = ref(false);

let decoder = null;
let frameCount = 0;
let visibilityObserver = null;
let frameTimer = null;
let hoverTarget = null;
let hasFinishedLoops = false;
// Cada arranque ganha um numero. Um ciclo antigo que acorde depois de o
// usuario ter rolado para fora e voltado descobre pelo numero que ja nao e o
// ciclo corrente e desiste, em vez de desenhar por cima do novo.
let runToken = 0;

const clearFrameTimer = () => {
  if (frameTimer) {
    clearTimeout(frameTimer);
    frameTimer = null;
  }
};

const drawFrame = async (index, token) => {
  if (token !== runToken || !decoder || !canvas.value) return null;

  const { image } = await decoder.decode({ frameIndex: index });
  if (token !== runToken || !canvas.value) {
    image.close();
    return null;
  }

  // Aceleres 2026-09-09: sem dimensao nao se assume o desenho.
  //
  // `displayWidth` e o campo que o Chrome preenche; nem toda implementacao de
  // WebCodecs preenche o mesmo. Se ele vier 0/undefined, `element.width = 0`
  // deixa o canvas com ZERO pixels: o `drawImage` seguinte nao lanca erro
  // nenhum, simplesmente nao desenha — e como o `<img>` ja foi escondido pelo
  // `active`, a figurinha SOME depois de um relance. Foi o relato de 09/09/2026
  // no Firefox 154 (a mesma figurinha aparecia normal para quem usava Chrome).
  //
  // Firefox tem `ImageDecoder` desde a 130, entao o guarda de suporte la em
  // cima nao protege este caso: a API existe, o decode corre, e o que falta e
  // so a medida.
  const width = image.displayWidth || image.codedWidth || 0;
  const height = image.displayHeight || image.codedHeight || 0;
  if (!width || !height) {
    image.close();
    return null;
  }

  const element = canvas.value;
  if (element.width !== width) {
    element.width = width;
    element.height = height;
  }

  const context = element.getContext('2d');
  context.clearRect(0, 0, element.width, element.height);
  context.drawImage(image, 0, 0);

  // `duration` vem em MICROsegundos; sem fecho explicito o quadro decodificado
  // fica retido e uma conversa com varias figurinhas acumula memoria.
  const duration = Number(image.duration) || 0;
  image.close();

  return Math.max(duration / 1000, 16);
};

// Aceleres 2026-09-09: desistir tem de devolver o `<img>`.
//
// Depois do `active` o `<img>` esta escondido e quem desenha e o canvas. Se um
// quadro seguinte falhar, ficar calado deixa a bolha VAZIA — que e pior do que
// nao ter animacao limitada. Aqui o componente admite que nao consegue e
// devolve o desenho ao `<img>`, que anima sozinho (em ciclo infinito, como
// antes desta melhoria existir).
const giveUp = () => {
  clearFrameTimer();
  if (!isActive.value) return;

  isActive.value = false;
  emit('inactive');
};

// Um passo por quadro, agendando o seguinte — em vez de um laco com `await`
// dentro. Assim o ciclo e sempre interrompivel: basta o `runToken` mudar (o
// usuario rolou para fora, o componente foi destruido) que o passo seguinte
// desiste sozinho, sem deixar nenhum laco a correr em segundo plano.
const step = async (token, loop, index) => {
  const delay = await drawFrame(index, token);
  if (delay === null) {
    if (token === runToken) giveUp();
    return;
  }

  if (!isActive.value) {
    isActive.value = true;
    emit('active');
  }

  const nextIndex = index + 1 < frameCount ? index + 1 : 0;
  const nextLoop = nextIndex === 0 ? loop + 1 : loop;
  // Gastas as 3 voltas, para no ultimo quadro desenhado.
  if (nextLoop >= STICKER_MAX_LOOPS) {
    hasFinishedLoops = true;
    return;
  }

  frameTimer = setTimeout(() => {
    if (token !== runToken) return;
    step(token, nextLoop, nextIndex).catch(giveUp);
  }, delay);
};

// Sair da tela para a animacao; voltar recomeca a contagem das 3 voltas.
// Mesma regra do GIF, para as duas midias animadas nao se comportarem de
// maneiras diferentes na mesma conversa.
const restart = () => {
  clearFrameTimer();
  hasFinishedLoops = false;
  runToken += 1;
  step(runToken, 0, 0).catch(giveUp);
};

const replayFinishedAnimation = () => {
  if (!props.replayOnHover || !hasFinishedLoops) return;
  restart();
};

// O canvas nao recebe eventos do mouse para nao bloquear o clique da figurinha.
// Por isso o hover acompanha a mesma moldura que ja contem imagem e canvas.
const watchHover = () => {
  if (!props.replayOnHover) return;
  hoverTarget = canvas.value?.parentElement || null;
  hoverTarget?.addEventListener('mouseenter', replayFinishedAnimation);
};

const watchVisibility = () => {
  if (typeof IntersectionObserver === 'undefined' || !canvas.value) return;

  visibilityObserver = new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting) {
        restart();
        return;
      }
      clearFrameTimer();
      runToken += 1;
    },
    { threshold: 0.1 }
  );
  visibilityObserver.observe(canvas.value);
};

onMounted(async () => {
  if (typeof window === 'undefined' || !window.ImageDecoder) return;
  if (!props.url || !canvas.value) return;
  // Aceleres 2026-09-09: a bolha otimista — a que existe entre o clique em
  // enviar e a resposta do servidor — aponta para um `blob:` do proprio
  // ficheiro. A CSP do painel tem `connect-src 'self' https: wss:`, sem
  // `blob:`, entao este fetch era recusado e cada figurinha enviada deixava
  // dois erros no console. Nao ha nada a recuperar aqui: o `<img>` ja desenha a
  // figurinha (animada, em ciclo infinito) durante os poucos segundos ate a URL
  // do servidor chegar, e ai o canvas assume e limita as voltas.
  if (props.url.startsWith('blob:')) return;

  try {
    const response = await fetch(props.url, { credentials: 'same-origin' });
    if (!response.ok) return;

    const data = await response.arrayBuffer();
    const type = response.headers.get('content-type') || 'image/webp';

    const candidate = new window.ImageDecoder({ data, type });
    await candidate.tracks.ready;

    const track = candidate.tracks.selectedTrack;
    // Figurinha parada nao tem voltas para limitar: deixa o `<img>` em paz.
    if (!track?.animated || Number(track.frameCount) < 2) {
      candidate.close();
      return;
    }

    decoder = candidate;
    frameCount = Number(track.frameCount);
    watchVisibility();
    watchHover();
    restart();
  } catch {
    decoder = null;
  }
});

onBeforeUnmount(() => {
  clearFrameTimer();
  runToken += 1;
  visibilityObserver?.disconnect();
  visibilityObserver = null;
  hoverTarget?.removeEventListener('mouseenter', replayFinishedAnimation);
  hoverTarget = null;
  decoder?.close();
  decoder = null;
});
</script>

<template>
  <canvas
    ref="canvas"
    class="pointer-events-none absolute inset-0 z-[1] size-full object-contain"
    :class="{ 'opacity-0': !isActive }"
    aria-hidden="true"
  />
</template>
